'use client';

import { ArrowUpRight, MapPin } from 'lucide-react';
import { googleMapsEmbedUrl } from '@sweetspot/shared';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

export function LocationDialog({ name, address, mapEmbedUrl, className }: {
  name: string;
  address: string;
  mapEmbedUrl?: unknown;
  className?: string;
}) {
  const embed = googleMapsEmbedUrl(mapEmbedUrl);
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, ${address}`)}`;
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" className={className}>Location</button>
      </DialogTrigger>
      <DialogContent
        className="max-h-[90svh] w-[calc(100%-2rem)] max-w-2xl gap-0 overflow-y-auto rounded-2xl border-0 bg-[#FAF7F0] p-0 text-[#1D1A16] sm:rounded-2xl"
        style={{ fontFamily: 'var(--font-site-body)' }}
      >
        <div className="px-6 pb-5 pt-7 sm:px-8">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#746E64]">Visit us</p>
          <DialogTitle className="pr-8 text-3xl font-normal italic" style={{ fontFamily: 'Georgia, serif' }}>{name}</DialogTitle>
          <DialogDescription className="mt-3 flex items-start gap-2 text-sm leading-relaxed text-[#625C53]">
            <MapPin aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{address}</span>
          </DialogDescription>
        </div>
        {embed && (
          <iframe
            title={`Map of ${name}`}
            src={embed}
            className="h-[min(40svh,340px)] min-h-[220px] w-full border-0"
            loading="lazy"
            tabIndex={-1}
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        )}
        <div className="px-6 py-5 sm:px-8">
          <a href={mapsLink} target="_blank" rel="noopener noreferrer"
            className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[#1D1A16] px-5 text-sm font-medium text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">
            Open in Google Maps <ArrowUpRight aria-hidden="true" className="h-4 w-4" />
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
